import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import { BLOCKED_PICKUP_DATES } from "./blockedDates";

const LOGO_SRC = "/logo.png";

// ─── ORDER CONFIG: Change dates here — updates everywhere ─────────────────────
const ORDER_CONFIG = {
  cutoffDay:  "Sunday",
  cutoffTime: "midnight",
  pickupDay:  "Tuesday",
};

// ─── SQUARE CONFIG ────────────────────────────────────────────────────────────
const SQUARE_CONFIG = {
  appId:      process.env.REACT_APP_SQUARE_APP_ID      || "sq0idp-u2eJUYg9dvUUajzAd24yLA",
  locationId: process.env.REACT_APP_SQUARE_LOCATION_ID || "L8D4WD34NRG6M",
  env:        process.env.REACT_APP_SQUARE_ENV         || "production",
};

const SQUARE_SDK_URL = "https://web.squarecdn.com/v1/square.js";

// ─── SUPABASE CLIENT ──────────────────────────────────────────────────────────
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const CATEGORIES = ["All", "Breads", "Baking Mixes", "Fresh Milled Flour", "Seasonings", "Beverages & Wellness", "Dough"];

const fmt = (p) => "$" + (p % 1 === 0 ? p.toFixed(0) : p.toFixed(2));
const fmtCents = (p) => Math.round(p * 100);

const style = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@300;400;500&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }
.gg-app { font-family: 'Jost', sans-serif; background: #FAF8F4; color: #1E1C18; min-height: 100vh; }
.gg-header { background: #FAF8F4; border-bottom: 1px solid #C8D5C0; padding: 16px 32px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 100; }
.gg-logo img { height: 68px; width: auto; display: block; }
.gg-cart-btn { background: #8C9E82; color: #FAF8F4; border: none; padding: 10px 20px; font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: background 0.2s; }
.gg-cart-btn:hover { background: #1E1C18; }
.gg-cart-badge { background: #FAF8F4; color: #8C9E82; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 500; }
.gg-hero { background: #F2EDE4; padding: 48px 32px 40px; border-bottom: 1px solid #C8D5C0; }
.gg-hero-inner { max-width: 720px; }
.gg-hero h1 { font-family: 'Cormorant Garamond', serif; font-weight: 300; font-size: 42px; color: #1E1C18; line-height: 1.15; margin-bottom: 12px; }
.gg-hero p { font-family: 'Jost', sans-serif; font-size: 14px; font-weight: 300; color: #1E1C18; line-height: 1.7; max-width: 520px; opacity: 0.8; }
.gg-pickup-banner { margin-top: 20px; display: inline-flex; align-items: center; background: #C8D5C0; padding: 8px 16px; font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: #1E1C18; }
.gg-main { display: grid; grid-template-columns: 1fr 340px; max-width: 1200px; margin: 0 auto; padding: 0 32px; align-items: start; }
@media (max-width: 900px) { .gg-main { grid-template-columns: 1fr; padding: 0 16px; } .gg-sidebar { display: none; } .gg-header { padding: 12px 16px; } .gg-hero { padding: 32px 16px; } .gg-hero h1 { font-size: 30px; } .gg-logo img { height: 52px; } }
.gg-products { padding: 40px 32px 40px 0; }
.gg-category-tabs { display: flex; margin-bottom: 36px; border-bottom: 1px solid #C8D5C0; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.gg-tab { font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; padding: 12px 16px; border: none; background: none; color: #1E1C18; opacity: 0.5; cursor: pointer; white-space: nowrap; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: all 0.2s; }
.gg-tab:hover { opacity: 0.8; }
.gg-tab.active { opacity: 1; border-bottom-color: #8C9E82; color: #8C9E82; }
.gg-category-label { font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 0.2em; text-transform: uppercase; color: #8C9E82; margin-bottom: 20px; }
.gg-product-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 16px; }
.gg-product-card { background: #F2EDE4; border: 1px solid transparent; padding: 20px; transition: border-color 0.2s, box-shadow 0.2s; }
.gg-product-card:hover { border-color: #8C9E82; box-shadow: 0 2px 12px rgba(140,158,130,0.12); }
.gg-product-card.in-cart { border-color: #8C9E82; }
.gg-product-name { font-family: 'Cormorant Garamond', serif; font-size: 18px; font-weight: 400; color: #1E1C18; line-height: 1.3; margin-bottom: 8px; }
.gg-product-desc { font-size: 12px; font-weight: 300; color: #1E1C18; opacity: 0.7; line-height: 1.6; margin-bottom: 10px; }
.gg-product-ingredients { font-size: 10px; font-weight: 400; letter-spacing: 0.04em; color: #8C9E82; margin-bottom: 16px; line-height: 1.5; font-style: italic; }
.gg-product-footer { display: flex; align-items: center; justify-content: space-between; padding-top: 12px; border-top: 1px solid #C8D5C0; }
.gg-product-price { font-family: 'Cormorant Garamond', serif; font-size: 20px; font-weight: 400; color: #1E1C18; }
.gg-qty-control { display: flex; align-items: center; gap: 8px; }
.gg-qty-btn { width: 28px; height: 28px; border: 1px solid #C8D5C0; background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 16px; color: #1E1C18; transition: all 0.15s; }
.gg-qty-btn:hover { border-color: #8C9E82; background: #C8D5C0; }
.gg-qty-btn.add { background: #8C9E82; border-color: #8C9E82; color: #FAF8F4; }
.gg-qty-btn.add:hover { background: #1E1C18; border-color: #1E1C18; }
.gg-qty-num { font-size: 14px; font-weight: 500; min-width: 20px; text-align: center; }
.gg-sidebar { padding: 40px 0 40px 32px; border-left: 1px solid #C8D5C0; position: sticky; top: 101px; max-height: calc(100vh - 101px); overflow-y: auto; }
.gg-sidebar-title { font-family: 'Jost', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 0.2em; text-transform: uppercase; color: #8C9E82; margin-bottom: 20px; }
.gg-cart-empty { font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 15px; color: #1E1C18; opacity: 0.45; line-height: 1.6; }
.gg-cart-item { display: flex; justify-content: space-between; align-items: start; padding: 12px 0; border-bottom: 1px solid #C8D5C0; gap: 8px; }
.gg-cart-item-name { font-family: 'Cormorant Garamond', serif; font-size: 15px; color: #1E1C18; line-height: 1.3; flex: 1; }
.gg-cart-item-meta { font-size: 11px; font-weight: 300; color: #8C9E82; margin-top: 2px; }
.gg-cart-remove { background: none; border: none; color: #1E1C18; opacity: 0.3; cursor: pointer; font-size: 16px; padding: 0 2px; }
.gg-cart-remove:hover { opacity: 0.7; }
.gg-cart-total { padding: 16px 0 20px; display: flex; justify-content: space-between; align-items: center; }
.gg-cart-total-label { font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: #1E1C18; opacity: 0.6; }
.gg-cart-total-amount { font-family: 'Cormorant Garamond', serif; font-size: 24px; font-weight: 400; color: #1E1C18; }
.gg-checkout-btn { width: 100%; background: #8C9E82; color: #FAF8F4; border: none; padding: 14px; font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; cursor: pointer; transition: background 0.2s; }
.gg-checkout-btn:hover { background: #1E1C18; }
.gg-checkout-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.gg-checkout-btn:disabled:hover { background: #8C9E82; }
.gg-trust { margin-top: 16px; font-size: 10px; font-weight: 300; color: #1E1C18; opacity: 0.5; line-height: 1.8; text-align: center; }
.gg-overlay { position: fixed; inset: 0; background: rgba(30,28,24,0.5); z-index: 200; display: flex; align-items: flex-end; justify-content: center; }
@media (min-width: 600px) { .gg-overlay { align-items: center; } }
.gg-drawer { background: #FAF8F4; width: 100%; max-width: 540px; max-height: 90vh; overflow-y: auto; padding: 32px; }
.gg-drawer-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 28px; }
.gg-drawer-title { font-family: 'Cormorant Garamond', serif; font-size: 28px; font-weight: 300; color: #1E1C18; }
.gg-close-btn { background: none; border: 1px solid #C8D5C0; width: 36px; height: 36px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 20px; color: #1E1C18; transition: border-color 0.2s; }
.gg-close-btn:hover { border-color: #8C9E82; }
.gg-form-group { margin-bottom: 20px; }
.gg-form-label { display: block; font-size: 10px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: #8C9E82; margin-bottom: 6px; }
.gg-form-input { width: 100%; border: 1px solid #C8D5C0; background: #F2EDE4; padding: 12px 14px; font-family: 'Jost', sans-serif; font-size: 14px; font-weight: 300; color: #1E1C18; outline: none; transition: border-color 0.2s; }
.gg-form-input:focus { border-color: #8C9E82; }
.gg-form-input::placeholder { color: #1E1C18; opacity: 0.35; }
.gg-section-divider { height: 1px; background: #C8D5C0; margin: 24px 0; }
.gg-payment-label { font-size: 10px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: #8C9E82; margin-bottom: 14px; display: block; }
#card-container { min-height: 90px; background: #F2EDE4; border: 1px solid #C8D5C0; padding: 14px; margin-bottom: 4px; }
#card-container.sq-loading { display: flex; align-items: center; justify-content: center; color: #8C9E82; font-size: 12px; font-family: 'Jost', sans-serif; font-weight: 300; opacity: 0.6; }
.gg-payment-lock { display: flex; align-items: center; gap: 5px; margin-top: 10px; font-size: 10px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: #8C9E82; opacity: 0.65; }
.gg-payment-error { font-size: 12px; font-weight: 300; color: #9B4444; margin-top: 8px; padding: 10px 12px; background: #fdf5f5; border: 1px solid #e8c4c4; }
.gg-payment-gate { background: #F2EDE4; border: 1px solid #C8D5C0; padding: 20px; text-align: center; }
.gg-order-summary-mini { background: #F2EDE4; border: 1px solid #C8D5C0; padding: 16px; margin-bottom: 24px; }
.gg-order-summary-mini-title { font-size: 10px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: #8C9E82; margin-bottom: 12px; }
.gg-order-summary-row { display: flex; justify-content: space-between; font-size: 13px; font-weight: 300; padding: 4px 0; color: #1E1C18; }
.gg-order-summary-total { display: flex; justify-content: space-between; align-items: baseline; margin-top: 10px; padding-top: 10px; border-top: 1px solid #C8D5C0; }
.gg-order-summary-total-label { font-size: 11px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: #1E1C18; opacity: 0.6; }
.gg-order-summary-total-amount { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 400; color: #1E1C18; }
.gg-confirmation { max-width: 560px; margin: 80px auto; padding: 0 32px; text-align: center; }
.gg-confirmation-icon { width: 56px; height: 56px; background: #C8D5C0; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; }
.gg-confirmation h2 { font-family: 'Cormorant Garamond', serif; font-size: 36px; font-weight: 300; color: #1E1C18; margin-bottom: 12px; }
.gg-confirmation p { font-size: 14px; font-weight: 300; color: #1E1C18; opacity: 0.7; line-height: 1.7; margin-bottom: 8px; }
.gg-confirmation-details { background: #F2EDE4; border: 1px solid #C8D5C0; padding: 20px; margin: 28px 0; text-align: left; }
.gg-start-over { background: none; border: 1px solid #C8D5C0; padding: 12px 24px; font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase; color: #1E1C18; cursor: pointer; transition: all 0.2s; }
.gg-start-over:hover { background: #C8D5C0; }
.gg-contact-link { font-family: 'Jost', sans-serif; font-size: 11px; font-weight: 300; letter-spacing: 0.1em; color: #8C9E82; text-decoration: none; opacity: 0.75; transition: opacity 0.2s; }
.gg-contact-link:hover { opacity: 1; }
`;

// ─── LOAD SQUARE SDK ──────────────────────────────────────────────────────────
// Module-level cache so the script is only ever appended once, even if the
// SquarePaymentForm mounts, unmounts, and remounts while the script is loading.
let _squareSdkPromise = null;
function loadSquareSDK() {
  if (window.Square) return Promise.resolve(window.Square);
  if (!_squareSdkPromise) {
    _squareSdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SQUARE_SDK_URL;
      script.onload = () => resolve(window.Square);
      script.onerror = () => {
        _squareSdkPromise = null; // allow retry on hard failure
        reject(new Error("Square SDK script failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return _squareSdkPromise;
}

// ─── PRODUCT CARD ─────────────────────────────────────────────────────────────
function ProductCard({ product, qty, setQty, breadsSoldOut }) {
  return (
    <div className={`gg-product-card ${qty > 0 ? "in-cart" : ""}`}>
      <div className="gg-product-name">{product.name}</div>
      <div className="gg-product-desc">{product.description}</div>
      <div className="gg-product-ingredients">{product.ingredients}</div>
      <div className="gg-product-footer">
        <span className="gg-product-price">{fmt(product.price)}</span>
        <div className="gg-qty-control">
          {(() => {
            const isBread = product.category === "Breads";
            const cannotAddMore = product.sold_out || (isBread && breadsSoldOut);
            if (cannotAddMore && qty === 0) {
              return <span style={{ fontFamily: "'Jost', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: "#9B4444", opacity: 0.7 }}>Sold Out</span>;
            }
            return qty > 0 ? (
              <>
                <button className="gg-qty-btn" onClick={() => setQty(product.id, qty - 1)}>−</button>
                <span className="gg-qty-num">{qty}</span>
                {!cannotAddMore && (
                  <button className="gg-qty-btn add" onClick={() => setQty(product.id, qty + 1)}>+</button>
                )}
              </>
            ) : (
              <button className="gg-qty-btn add" onClick={() => setQty(product.id, 1)}>+</button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─── SQUARE PAYMENT FORM ──────────────────────────────────────────────────────
function SquarePaymentForm({ onTokenize, loading, error, cartTotal = 0 }) {
  const cardRef = useRef(null);
  const mountedRef = useRef(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    let card;
    loadSquareSDK()
      .then(async (Square) => {
        if (!mountedRef.current) return;
        const payments = Square.payments(SQUARE_CONFIG.appId, SQUARE_CONFIG.locationId);
        card = await payments.card({
          style: {
            ".input-container": { borderColor: "#C8D5C0", borderRadius: "0px" },
            ".input-container.is-focus": { borderColor: "#8C9E82" },
            ".input-container.is-error": { borderColor: "#9B4444" },
            input: { color: "#1E1C18" },
          },
        });
        if (!mountedRef.current) return;
        // Verify the container is still in the DOM before attaching — it can be
        // removed if canCheckout flips false while payments.card() was awaiting.
        if (!document.getElementById("card-container")) {
          throw new Error("card-container not found — component unmounted during initialization");
        }
        await card.attach("#card-container");
        if (!mountedRef.current) return;
        cardRef.current = card;
        setSdkReady(true);
      })
      .catch((err) => {
        console.error("Square SDK error:", err.message || err);
        if (mountedRef.current) {
          setSdkError("Payment form failed to load. Please refresh and try again.");
        }
      });

    return () => {
      if (card) card.destroy().catch(() => {});
      mountedRef.current = false;
    };
  }, []);

  const handlePay = async () => {
    if (!cardRef.current) return;
    const result = await cardRef.current.tokenize();
    if (result.status === "OK") {
      onTokenize(result.token);
    } else {
      const msg = result.errors?.map(e => e.message).join(", ") || "Card declined. Please try again.";
      onTokenize(null, msg);
    }
  };

  return (
    <div>
      <div id="card-container" className={!sdkReady && !sdkError ? "sq-loading" : ""}>
        {!sdkReady && !sdkError && "Loading secure payment form…"}
        {sdkError && <span style={{ color: "#9B4444", fontSize: 12 }}>{sdkError}</span>}
      </div>
      <div className="gg-payment-lock">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#8C9E82" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        Secured by Square · 2.6% + 10¢ per transaction
      </div>
      {error && <div className="gg-payment-error">{error}</div>}
      {sdkReady && (
        <div style={{ marginTop: 20 }}>
          <button className="gg-checkout-btn" disabled={loading} onClick={handlePay}>
            {loading ? "Processing…" : `Place Order · ${fmt(cartTotal)}`}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [cart, setCart] = useState({});
  const [activeCategory, setActiveCategory] = useState("All");
  const [showCheckout, setShowCheckout] = useState(false);
  const [view, setView] = useState("shop");
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "", notes: "", coverFee: false });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [orderRef, setOrderRef] = useState(null);
  const [pickupDate, setPickupDate] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState(null);
  const [storeSettings, setStoreSettings] = useState({ orderingPausedFrom: null, orderingPausedUntil: null, skipPickupWeek: null });

  // ── Load products from Supabase ───────────────────────────────────────────
  useEffect(() => {
    supabase
      .from("products")
      .select("*")
      .eq("visible", true)
      .order("sort_order")
      .then(({ data, error }) => {
        if (error) {
          setProductsError("Couldn't load products. Please refresh to try again.");
        } else {
          setProducts(data || []);
        }
        setProductsLoading(false);
      });
  }, []);

  // ── Load store-wide toggles (pause ordering / skip a pickup week) ─────────
  useEffect(() => {
    supabase
      .from("store_settings")
      .select("ordering_paused_from, ordering_paused_until, skip_pickup_week")
      .eq("id", 1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setStoreSettings({
            orderingPausedFrom: data.ordering_paused_from || null,
            orderingPausedUntil: data.ordering_paused_until || null,
            skipPickupWeek: data.skip_pickup_week || null,
          });
        }
      });
  }, []);

  // ── Discount codes — add/remove codes here ───────────────────────────────
  const DISCOUNT_CODES = {
    "FAMILY":     { type: "percent", value: 40,  label: "40% off"  },
    "WELCOME10":  { type: "percent", value: 10,  label: "10% off"  },
    "FRIEND15":   { type: "percent", value: 15,  label: "15% off"  },
    "FIRSTORDER": { type: "percent", value: 20,  label: "20% off"  },
    "HPCSA2026":  { type: "fixed",   value: 36,  label: "$36 off"  },
    "TOTAL100":   { type: "percent", value: 100, label: "100% off" },
  };

  const applyDiscount = () => {
    const code = discountCode.trim().toUpperCase();
    if (DISCOUNT_CODES[code]) {
      setAppliedDiscount({ code, ...DISCOUNT_CODES[code] });
      setDiscountError(null);
    } else {
      setDiscountError("That code isn't valid. Please check and try again.");
      setAppliedDiscount(null);
    }
  };

  // Generate next 4 Tuesdays whose order window is still open.
  // Cutoff is Sunday at midnight (= Monday 00:00:00, one day before Tuesday).
  // Any Tuesday where that cutoff has already passed is skipped.
  const getNextTuesdays = () => {
    const tuesdays = [];
    const now = new Date();
    const day = now.getDay();
    const daysUntilTuesday = day <= 2 ? 2 - day : 9 - day;
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + daysUntilTuesday);
    candidate.setHours(0, 0, 0, 0);
    while (tuesdays.length < 4) {
      const cutoff = new Date(candidate);
      cutoff.setDate(candidate.getDate() - 1); // Monday 00:00:00 = Sunday midnight
      const iso = `${candidate.getFullYear()}-${String(candidate.getMonth() + 1).padStart(2, "0")}-${String(candidate.getDate()).padStart(2, "0")}`;
      if (now < cutoff && !BLOCKED_PICKUP_DATES.includes(iso) && iso !== storeSettings.skipPickupWeek) {
        tuesdays.push(new Date(candidate));
      }
      candidate.setDate(candidate.getDate() + 7);
    }
    return tuesdays;
  };
  const tuesdays = getNextTuesdays();
  const isCutoffWarning = new Date().getDay() === 0;
  // Live date-range check (not a cached flag) so a future-scheduled pause
  // automatically takes effect on the right day with no cron involved.
  const todayISO = new Date().toISOString().slice(0, 10);
  const isOrderingPausedToday =
    !!storeSettings.orderingPausedFrom &&
    todayISO >= storeSettings.orderingPausedFrom &&
    (!storeSettings.orderingPausedUntil || todayISO <= storeSettings.orderingPausedUntil);
  const fmtDate = (d) => d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const fmtDateShort = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const cartTotal = Object.entries(cart).reduce((sum, [id, qty]) => {
    const p = products.find(p => String(p.id) === id);
    return sum + (p ? p.price * qty : 0);
  }, 0);
  const cartItems = Object.entries(cart)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => ({ ...products.find(p => String(p.id) === id), qty }))
    .filter(item => item.id);

  const setQty = (id, qty) => {
    setCart(prev => {
      const next = { ...prev };
      const key = String(id);
      if (qty <= 0) delete next[key];
      else next[key] = qty;
      return next;
    });
  };

  const filtered = activeCategory === "All" ? products : products.filter(p => p.category === activeCategory);

  // ── Bread limit — total loaves across all bread products combined ──────────
  const BREAD_LIMIT = 12;
  const breadCount = Object.entries(cart).reduce((sum, [id, qty]) => {
    const p = products.find(p => String(p.id) === id);
    return sum + (p && p.category === "Breads" ? qty : 0);
  }, 0);
  const breadsRemaining = BREAD_LIMIT - breadCount;
  const breadsSoldOut = breadsRemaining <= 0;

  const canCheckout = customerInfo.name.trim() && customerInfo.email.trim() && pickupDate;
  const processingFee = parseFloat((cartTotal * 0.026 + 0.10).toFixed(2));
  const discountAmount = appliedDiscount
    ? appliedDiscount.type === "fixed"
      ? parseFloat(Math.min(appliedDiscount.value, cartTotal).toFixed(2))
      : parseFloat((cartTotal * appliedDiscount.value / 100).toFixed(2))
    : 0;
  const discountedTotal = cartTotal - discountAmount;
  const orderTotal = customerInfo.coverFee ? discountedTotal + processingFee : discountedTotal;

  // ── Called by SquarePaymentForm after card tokenization ───────────────────
  const handleTokenize = async (token, tokenError) => {
    if (tokenError) { setPaymentError(tokenError); return; }
    if (!token) return;
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const res = await fetch("/api/create-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourceId: token,
          amountMoney: { amount: fmtCents(orderTotal), currency: "USD" },
          customer: customerInfo,
          lineItems: cartItems.map(i => ({ name: i.name, quantity: i.qty, price: i.price })),
          pickupDate: pickupDate ? pickupDate.toISOString() : null,
          discount: appliedDiscount ? { code: appliedDiscount.code, label: appliedDiscount.label, amount: discountAmount } : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Payment failed");
      setOrderRef(data.orderId);
      setShowCheckout(false);
      setView("confirmation");
    } catch (err) {
      setPaymentError(err.message || "Something went wrong. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // ── Called when order total is $0 — skips Square, still saves order ───────
  const handleFreeOrder = async () => {
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const res = await fetch("/api/create-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourceId: null,
          amountMoney: { amount: 0, currency: "USD" },
          customer: customerInfo,
          lineItems: cartItems.map(i => ({ name: i.name, quantity: i.qty, price: i.price })),
          pickupDate: pickupDate ? pickupDate.toISOString() : null,
          discount: appliedDiscount ? { code: appliedDiscount.code, label: appliedDiscount.label, amount: discountAmount } : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Order failed");
      setOrderRef(data.orderId);
      setShowCheckout(false);
      setView("confirmation");
    } catch (err) {
      setPaymentError(err.message || "Something went wrong. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // ── CONFIRMATION ──────────────────────────────────────────────────────────
  if (view === "confirmation") {
    return (
      <div className="gg-app">
        <style>{style}</style>
        <header className="gg-header">
          <div className="gg-logo"><img src={LOGO_SRC} alt="Grind and Gather" /></div>
          <a href="mailto:grindgather@gmail.com" className="gg-contact-link" style={{ marginLeft: "auto" }}>Get in Touch</a>
        </header>
        <div className="gg-confirmation">
          <div className="gg-confirmation-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8C9E82" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2>Order Received</h2>
          <p>Thank you, {customerInfo.name.split(" ")[0]}. Your order is confirmed.</p>
          <p>A confirmation is on its way to {customerInfo.email}.</p>
          {orderRef && <p style={{ fontSize: 11, opacity: 0.4, marginTop: 4 }}>Ref: {orderRef}</p>}
          <div className="gg-confirmation-details">
            <div className="gg-order-summary-mini-title">Your Order</div>
            {cartItems.map(item => (
              <div key={item.id} className="gg-order-summary-row">
                <span>{item.name} × {item.qty}</span>
                <span>{fmt(item.price * item.qty)}</span>
              </div>
            ))}
            <div className="gg-order-summary-total">
              <span className="gg-order-summary-total-label">Total</span>
              <span className="gg-order-summary-total-amount">{fmt(orderTotal)}</span>
            </div>
          </div>
          <p style={{ fontSize: 12, opacity: 0.5, marginBottom: 24 }}>
            Pickup {pickupDate ? fmtDate(pickupDate) : ORDER_CONFIG.pickupDay}. We will be in touch with details.
          </p>
          <button className="gg-start-over" onClick={() => {
            setCart({}); setView("shop"); setOrderRef(null);
            setCustomerInfo({ name: "", email: "", phone: "", notes: "", coverFee: false });
          }}>Place Another Order</button>
        </div>
      </div>
    );
  }

  // ── ORDERING PAUSED ───────────────────────────────────────────────────────
  if (isOrderingPausedToday) {
    return (
      <div className="gg-app">
        <style>{style}</style>
        <header className="gg-header">
          <div className="gg-logo"><img src={LOGO_SRC} alt="Grind and Gather" /></div>
          <a href="mailto:grindgather@gmail.com" className="gg-contact-link" style={{ marginLeft: "auto" }}>Get in Touch</a>
        </header>
        <div className="gg-confirmation">
          <h2>We've Reached Capacity</h2>
          <p>We're not accepting new orders for this round — check back soon!</p>
        </div>
      </div>
    );
  }

  // ── MAIN SHOP ─────────────────────────────────────────────────────────────
  return (
    <div className="gg-app">
      <style>{style}</style>

      <header className="gg-header">
        <a href="mailto:grindgather@gmail.com" className="gg-contact-link">Get in Touch</a>
        <a href="/subscription/signup" className="gg-contact-link" style={{ marginLeft: 24 }}>Weekly Basket</a>
        <a href="/subscription/access" className="gg-contact-link" style={{ marginLeft: 16 }}>Access My Account</a>
        <button className="gg-cart-btn" onClick={() => cartCount > 0 && setShowCheckout(true)} style={{ marginLeft: "auto" }}>
          Order {cartCount > 0 && <span className="gg-cart-badge">{cartCount}</span>}
        </button>
      </header>

      <div className="gg-hero">
        <div className="gg-hero-inner" style={{ display: "flex", alignItems: "center", gap: 40 }}>
          <img src={LOGO_SRC} alt="Grind and Gather" style={{ height: 160, width: "auto", flexShrink: 0 }} />
          <div>
            <h1>Fresh-milled.<br />Small-batch.<br />Made this week.</h1>
            <p>Every product starts with freshly milled organic flour and real ingredients — nothing your grandmother wouldn't recognize. Orders close {ORDER_CONFIG.cutoffDay} at {ORDER_CONFIG.cutoffTime}. Pickup {ORDER_CONFIG.pickupDay}.</p>
            <div className="gg-pickup-banner">◦ Orders close {ORDER_CONFIG.cutoffDay} at {ORDER_CONFIG.cutoffTime} · Pickup {ORDER_CONFIG.pickupDay} ◦</div>
          </div>
        </div>
      </div>

      <div className="gg-main">
        <div className="gg-products">
          <div className="gg-category-tabs">
            {CATEGORIES.map(cat => (
              <button key={cat} className={`gg-tab ${activeCategory === cat ? "active" : ""}`} onClick={() => setActiveCategory(cat)}>{cat}</button>
            ))}
          </div>

          {productsLoading && (
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 18, color: "#1E1C18", opacity: 0.45, lineHeight: 1.6 }}>
              Loading this week's menu…
            </p>
          )}

          {productsError && (
            <p style={{ fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 300, color: "#9B4444", background: "#fdf5f5", border: "1px solid #e8c4c4", padding: "14px 16px" }}>
              {productsError}
            </p>
          )}

          {!productsLoading && !productsError && (
            activeCategory === "All" ? (
              CATEGORIES.slice(1).map(cat => {
                const items = products.filter(p => p.category === cat);
                if (items.length === 0) return null;
                return (
                  <div key={cat} style={{ marginBottom: 48 }}>
                    <div className="gg-category-label">{cat}</div>
                    <div className="gg-product-grid">
                      {items.map(product => <ProductCard key={product.id} product={product} qty={cart[String(product.id)] || 0} setQty={setQty} breadsSoldOut={breadsSoldOut} />)}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="gg-product-grid">
                {filtered.map(product => <ProductCard key={product.id} product={product} qty={cart[String(product.id)] || 0} setQty={setQty} breadsSoldOut={breadsSoldOut} />)}
              </div>
            )
          )}
        </div>

        <div className="gg-sidebar">
          <div className="gg-sidebar-title">Your Order</div>
          {cartItems.length === 0 ? (
            <p className="gg-cart-empty">Nothing added yet. Browse the menu and tap + to add items.</p>
          ) : (
            <>
              {cartItems.map(item => (
                <div key={item.id} className="gg-cart-item">
                  <div style={{ flex: 1 }}>
                    <div className="gg-cart-item-name">{item.name}</div>
                    <div className="gg-cart-item-meta">Qty {item.qty} · {fmt(item.price * item.qty)}</div>
                  </div>
                  <button className="gg-cart-remove" onClick={() => setQty(item.id, 0)}>×</button>
                </div>
              ))}
              <div className="gg-cart-total">
                <span className="gg-cart-total-label">Total</span>
                <span className="gg-cart-total-amount">{fmt(cartTotal)}</span>
              </div>
              <button className="gg-checkout-btn" onClick={() => setShowCheckout(true)}>Proceed to Checkout</button>
              <div className="gg-trust">No preservatives. No additives.<br />Every ingredient is organic.</div>
            </>
          )}
        </div>
      </div>

      {showCheckout && (
        <div className="gg-overlay" onClick={e => e.target === e.currentTarget && setShowCheckout(false)}>
          <div className="gg-drawer">
            <div className="gg-drawer-header">
              <h2 className="gg-drawer-title">Complete Your Order</h2>
              <button className="gg-close-btn" onClick={() => setShowCheckout(false)}>×</button>
            </div>

            <div className="gg-order-summary-mini">
              <div className="gg-order-summary-mini-title">Order Summary</div>
              {pickupDate && (
                <div className="gg-order-summary-row" style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid #C8D5C0" }}>
                  <span style={{ color: "#8C9E82" }}>Pickup date</span>
                  <span style={{ fontWeight: 500 }}>{fmtDate(pickupDate)}</span>
                </div>
              )}
              {cartItems.map(item => (
                <div key={item.id} className="gg-order-summary-row">
                  <span>{item.name} × {item.qty}</span>
                  <span>{fmt(item.price * item.qty)}</span>
                </div>
              ))}
              {appliedDiscount && (
                <div className="gg-order-summary-row">
                  <span style={{ color: "#8C9E82" }}>Discount ({appliedDiscount.label})</span>
                  <span style={{ color: "#8C9E82" }}>−{fmt(discountAmount)}</span>
                </div>
              )}
              {customerInfo.coverFee && (
                <div className="gg-order-summary-row">
                  <span style={{ color: "#8C9E82" }}>Processing fee</span>
                  <span>{fmt(processingFee)}</span>
                </div>
              )}
              <div className="gg-order-summary-total">
                <span className="gg-order-summary-total-label">Total</span>
                <span className="gg-order-summary-total-amount">{fmt(orderTotal)}</span>
              </div>
            </div>

            {/* Pickup date picker */}
            <div className="gg-form-group">
              <label className="gg-form-label">Pickup Date</label>
              {isCutoffWarning && (
                <div style={{ background: "#F2EDE4", border: "1px solid #8C9E82", padding: "10px 14px", marginBottom: 10, fontFamily: "'Jost', sans-serif", fontSize: 11, fontWeight: 400, color: "#1E1C18" }}>
                  Orders for this Tuesday close tonight at midnight.
                </div>
              )}
              {storeSettings.skipPickupWeek && new Date(storeSettings.skipPickupWeek + "T00:00:00Z") >= new Date(new Date().toDateString()) && (
                <div style={{ background: "#F2EDE4", border: "1px solid #8C9E82", padding: "10px 14px", marginBottom: 10, fontFamily: "'Jost', sans-serif", fontSize: 11, fontWeight: 400, color: "#1E1C18" }}>
                  No pickup the week of {new Date(storeSettings.skipPickupWeek + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })} — we'll be back the following Tuesday.
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {tuesdays.map((d, i) => (
                  <button key={i} onClick={() => setPickupDate(d)} style={{
                    background: pickupDate && pickupDate.getTime() === d.getTime() ? "#C8D5C0" : "#F2EDE4",
                    border: pickupDate && pickupDate.getTime() === d.getTime() ? "1px solid #8C9E82" : "1px solid #C8D5C0",
                    padding: "12px 16px", textAlign: "left", cursor: "pointer",
                    fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 400, color: "#1E1C18",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <span>{fmtDate(d)}</span>
                    <span style={{ fontFamily: "'Jost', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: "#8C9E82" }}>
                      {i === 0 ? "Next pickup" : `+${i} week${i > 1 ? "s" : ""}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {[
              { key: "name",  label: "Full Name",              type: "text",  placeholder: "Sara Smith" },
              { key: "email", label: "Email Address",          type: "email", placeholder: "sara@example.com" },
              { key: "phone", label: "Phone Number",           type: "tel",   placeholder: "(555) 000-0000" },
              { key: "notes", label: "Order Notes (optional)", type: "text",  placeholder: "Allergies, substitutions, questions?" },
            ].map(field => (
              <div key={field.key} className="gg-form-group">
                <label className="gg-form-label">{field.label}</label>
                <input className="gg-form-input" type={field.type} placeholder={field.placeholder}
                  value={customerInfo[field.key]} onChange={e => setCustomerInfo(p => ({ ...p, [field.key]: e.target.value }))} />
              </div>
            ))}

            {/* Discount code */}
            <div className="gg-form-group">
              <label className="gg-form-label">Discount Code</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  className="gg-form-input"
                  type="text"
                  placeholder="Enter code"
                  value={discountCode}
                  onChange={e => { setDiscountCode(e.target.value); setDiscountError(null); }}
                  style={{ flex: 1 }}
                />
                <button onClick={applyDiscount} style={{
                  background: "#8C9E82", color: "#FAF8F4", border: "none",
                  padding: "0 16px", fontFamily: "'Jost', sans-serif",
                  fontSize: 11, fontWeight: 500, letterSpacing: "0.12em",
                  textTransform: "uppercase", cursor: "pointer", flexShrink: 0,
                }}>Apply</button>
              </div>
              {discountError && <p style={{ fontFamily: "'Jost', sans-serif", fontSize: 11, color: "#9B4444", marginTop: 6 }}>{discountError}</p>}
              {appliedDiscount && <p style={{ fontFamily: "'Jost', sans-serif", fontSize: 11, color: "#8C9E82", marginTop: 6 }}>✓ {appliedDiscount.label} applied</p>}
            </div>

            <div className="gg-form-group" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "#F2EDE4", border: "1px solid #C8D5C0", padding: "14px 16px" }}>
              <span style={{ fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 300, color: "#1E1C18", lineHeight: 1.6 }}>
                Want this every week?
              </span>
              <button
                type="button"
                onClick={() => window.open("/subscription/signup", "_blank", "noopener")}
                style={{
                  background: "none", border: "1px solid #8C9E82", color: "#8C9E82",
                  padding: "10px 16px", fontFamily: "'Jost', sans-serif", fontSize: 11, fontWeight: 500,
                  letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", flexShrink: 0,
                }}
              >
                Set Up a Weekly Basket
              </button>
            </div>

            <div className="gg-form-group" style={{ display: "flex", alignItems: "flex-start", gap: 12, background: "#F2EDE4", border: "1px solid #C8D5C0", padding: "14px 16px" }}>
              <input
                type="checkbox"
                id="coverFee"
                checked={customerInfo.coverFee}
                onChange={e => setCustomerInfo(p => ({ ...p, coverFee: e.target.checked }))}
                style={{ marginTop: 2, accentColor: "#8C9E82", width: 16, height: 16, flexShrink: 0, cursor: "pointer" }}
              />
              <label htmlFor="coverFee" style={{ fontFamily: "'Jost', sans-serif", fontSize: 13, fontWeight: 300, color: "#1E1C18", lineHeight: 1.6, cursor: "pointer" }}>
                Cover the processing fee ({fmt(processingFee)}) — helps us keep prices where they are.
              </label>
            </div>

            <div className="gg-section-divider" />
            <span className="gg-payment-label">Payment</span>

            {canCheckout ? (
              orderTotal === 0 ? (
                <div>
                  {paymentError && <div className="gg-payment-error">{paymentError}</div>}
                  <div style={{ marginTop: 20 }}>
                    <button className="gg-checkout-btn" disabled={paymentLoading} onClick={handleFreeOrder}>
                      {paymentLoading ? "Processing…" : "Place Order · $0"}
                    </button>
                  </div>
                </div>
              ) : (
                <SquarePaymentForm
                  onTokenize={handleTokenize}
                  loading={paymentLoading}
                  error={paymentError}
                  cartTotal={orderTotal}
                />
              )
            ) : (
              <div className="gg-payment-gate">
                <p style={{ fontFamily: "'Jost', sans-serif", fontSize: 12, fontWeight: 300, color: "#1E1C18", opacity: 0.5 }}>
                  {!pickupDate
                    ? "Choose a pickup date above to unlock payment."
                    : "Enter your name and email above to unlock payment."}
                </p>
              </div>
            )}

            <div className="gg-trust" style={{ marginTop: 16 }}>
              No preservatives · No additives · Every ingredient is organic
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
